import { collect } from "../../graph/paging.ts";
import { MODULE_SCOPES } from "../../auth/config.ts";
import { InteractionRequiredError, NotSignedInError } from "../../auth/broker.ts";
import { GraphError, graphFetch } from "../../graph/client.ts";
import { writeFile } from "../../storage/fsa.ts";
import { itemNeedsExport, putItem } from "../../storage/idb.ts";
import type { RunLogger } from "../../export/run.ts";
import { ExportAbortedError, throwIfAborted } from "../../export/abort.ts";
import { withinRange, isContainerSelected, type DateRange, type ContainerSelection } from "../../export/plan.ts";
import { notebookKey, sectionKey, pageKey, pagePath } from "./paths.ts";
import { fetchPageImages } from "./resources.ts";
import { sanitizePage } from "./sanitize.ts";
import { renderPage } from "./render.ts";
import { renderOneNoteIndex, type OneNotePageEntry } from "./index-render.ts";
import type { Notebook, OneNoteSection, OneNotePage } from "./types.ts";

/**
 * OneNote export (ARCHITECTURE.md §15 step 8).
 *
 * No `/delta` endpoint for any of notebooks, sections or pages, so every run
 * fully re-lists the notebook/section/page tree — the same "always re-list,
 * cost is trivial relative to Mail/OneDrive's volume" shape as Tasks. Unlike
 * Tasks, though, each *page* is genuinely resumable: a page is only ever
 * fetched and written once, keyed by its own Graph id, exactly like an
 * OneDrive file or a Teams message — a personal notebook can run into the
 * hundreds of pages, image-heavy, so re-downloading a page (and every image
 * on it) that a previous run already wrote would be real, avoidable cost.
 *
 * Only sections directly under a notebook are walked — nested section
 * groups are not (see paths.ts).
 */

const SCOPES = MODULE_SCOPES.onenote;

export interface OneNoteProgress {
    pagesDone: number;
    currentLabel?: string;
    totalBytes: number;
}

export type OneNoteProgressCallback = (progress: OneNoteProgress) => void;

export interface OneNotePageSummary {
    label: string;
    bytes: number;
}

export interface ExportOneNoteParams {
    runId: string;
    root: FileSystemDirectoryHandle;
    /** From the Scan step: only export these sections (ids). */
    selection?: ContainerSelection;
    /** From the Scan step: skip pages last modified outside this window. */
    dateRange?: DateRange;
    onProgress?: OneNoteProgressCallback;
    onPageComplete?: (summary: OneNotePageSummary) => void;
    /** Aborts at the next clean checkpoint. */
    signal?: AbortSignal;
    logger: RunLogger;
}

export interface OneNoteResult {
    pages: number;
    bytes: number;
    errors: number;
}

export async function exportOneNote(params: ExportOneNoteParams): Promise<OneNoteResult> {
    const { runId, root, selection, dateRange, onProgress, onPageComplete, signal, logger } = params;

    await logger.info("listing notebooks");
    const notebooks = await collect<Notebook>("/me/onenote/notebooks", {
        scopes: SCOPES,
        service: "outlook",
        ...(signal ? { signal } : {})
    });
    await logger.info(`found ${notebooks.length} notebooks`);

    let pages = 0;
    let bytes = 0;
    let errors = 0;
    const pageEntries: OneNotePageEntry[] = [];

    for (const notebook of notebooks) {
        throwIfAborted(signal);
        const nbKey = await notebookKey(notebook);

        let sections: OneNoteSection[];
        try {
            sections = await collect<OneNoteSection>(
                `/me/onenote/notebooks/${notebook.id}/sections`,
                { scopes: SCOPES, service: "outlook", ...(signal ? { signal } : {}) }
            );
        } catch (err) {
            if (
                err instanceof ExportAbortedError ||
                err instanceof InteractionRequiredError ||
                err instanceof NotSignedInError
            ) {
                throw err;
            }
            // One inaccessible notebook shouldn't fail the module.
            errors += 1;
            const message = err instanceof GraphError ? err.toLogString() : String(err);
            await logger.warn(`notebook sections listing failed, skipped: ${message}`);
            continue;
        }

        for (const section of sections) {
            throwIfAborted(signal);
            // Scan-step selection is at the section level.
            if (!isContainerSelected(selection, section.id)) {
                continue;
            }
            const secKey = await sectionKey(section);

            let sectionPages: OneNotePage[];
            try {
                sectionPages = await collect<OneNotePage>(
                    `/me/onenote/sections/${section.id}/pages?$select=id,title,lastModifiedDateTime`,
                    { scopes: SCOPES, service: "outlook", ...(signal ? { signal } : {}) }
                );
            } catch (err) {
                if (
                    err instanceof ExportAbortedError ||
                    err instanceof InteractionRequiredError ||
                    err instanceof NotSignedInError
                ) {
                    throw err;
                }
                // A password-protected or permission-restricted section (403)
                // is skipped rather than failing the whole OneNote export.
                errors += 1;
                const message = err instanceof GraphError ? err.toLogString() : String(err);
                await logger.warn(`section pages listing failed, skipped: ${message}`);
                continue;
            }

            for (const page of sectionPages) {
                throwIfAborted(signal);
                if (dateRange && !withinRange(page.lastModifiedDateTime, dateRange)) {
                    continue;
                }

                const label = `${notebook.displayName} / ${section.displayName} / ${page.title ?? "Untitled page"}`;
                const pgKey = await pageKey(page);
                const entry: OneNotePageEntry = {
                    notebook: notebook.displayName,
                    notebookKey: nbKey,
                    section: section.displayName,
                    sectionKey: secKey,
                    title: page.title ?? "Untitled page",
                    pageKey: pgKey
                };

                // An edited page has a newer lastModifiedDateTime and is
                // fetched and written again.
                const version = page.lastModifiedDateTime;
                if (!(await itemNeedsExport(runId, "onenote", page.id, version))) {
                    // Already on disk from an earlier run — the index still
                    // needs to list it, even though nothing is fetched again.
                    pageEntries.push(entry);
                    continue;
                }

                onProgress?.({ pagesDone: pages, currentLabel: label, totalBytes: bytes });

                try {
                    const contentResponse = await graphFetch(`/me/onenote/pages/${page.id}/content`, {
                        scopes: SCOPES,
                        service: "outlook",
                        ...(signal ? { signal } : {})
                    });
                    const contentHtml = await contentResponse.text();

                    const images = await fetchPageImages({
                        contentHtml,
                        notebook: nbKey,
                        section: secKey,
                        page: pgKey,
                        root,
                        scopes: SCOPES,
                        ...(signal ? { signal } : {}),
                        onWarn: (message) => logger.warn(`${message} (${page.id})`)
                    });

                    const sanitized = sanitizePage(contentHtml, images.urlMap);
                    const html = renderPage(sanitized.title, sanitized.bodyHtml);
                    const written = await writeFile(root, pagePath(nbKey, secKey, pgKey), html);
                    await putItem({
                        runId,
                        module: "onenote",
                        itemId: page.id,
                        status: "done",
                        bytes: written + images.bytes,
                        ...(version !== undefined ? { version } : {})
                    });

                    pages += 1;
                    bytes += written + images.bytes;
                    pageEntries.push(entry);
                    onPageComplete?.({ label, bytes: written + images.bytes });
                } catch (err) {
                    if (
                        err instanceof ExportAbortedError ||
                        err instanceof InteractionRequiredError ||
                        err instanceof NotSignedInError
                    ) {
                        throw err;
                    }
                    errors += 1;
                    const message = err instanceof GraphError ? err.toLogString() : String(err);
                    await logger.error(`page failed, will retry on resume: ${message} (${page.id})`);
                    onPageComplete?.({ label, bytes: 0 });
                }
            }
        }
    }

    // Idempotent and network-free, like every other module's index pass —
    // rebuilt each run from the pages seen above, whether new or already done.
    await renderOneNoteIndex(root, pageEntries);

    onProgress?.({ pagesDone: pages, totalBytes: bytes });
    await logger.info(`onenote export complete: ${pages} pages, ${errors} errors`);

    return { pages, bytes, errors };
}
